import type { TrackingEvent } from '../types/TrackingEvents'
import { getCalendarGroupElementId, getTrackingEventGroupId } from './trackingEventGroupId'

type HighlightedCalendarGroupElementIdsParams = {
  trackedGameId?: string
  eventType?: string
  highlightedEventId?: string
  gameEvents?: Pick<TrackingEvent, 'eventId' | 'typeId' | 'blueprintEvolutionData'>[]
  groupByBlueprint?: boolean
}

/**
 * Resolves the candidate DOM ids of the calendar group rows that contain a highlighted event.
 *
 * The highlight state only carries the game id and the plain event type id, but the row id depends on the view
 * (see `getTrackingEventGroupId` and `useCalendarEventsByEventType`):
 *   - Games tab, "Group by Live Event Blueprint" off: `${gameId}-${typeId}`
 *   - Games tab, grouping on: `${gameId}-${typeId}_${blueprintId}`; events without a blueprint keep `${gameId}-${typeId}`
 *   - Compare Events tab: `${gameId}-${typeId}_${gameId}`
 *
 * With grouping on, the blueprint of each highlighted instance is read from the loaded events. Until the highlighted
 * event is loaded its blueprint is unknown, so only the Compare Events row is a candidate; the plain type row is not,
 * because with grouping on it belongs to the events of that type that have no blueprint.
 */
export const highlightedCalendarGroupElementIds = ({
  trackedGameId,
  eventType,
  highlightedEventId,
  gameEvents = [],
  groupByBlueprint = false,
}: HighlightedCalendarGroupElementIdsParams): string[] => {
  if (!trackedGameId || !eventType) return []

  const instanceIds = groupByBlueprint
    ? gameEvents
        .filter((event) => !!highlightedEventId && event.eventId === highlightedEventId)
        .map((event) => getCalendarGroupElementId(trackedGameId, getTrackingEventGroupId(event, true)))
    : []

  const candidates = [
    ...instanceIds,
    ...(!groupByBlueprint ? [getCalendarGroupElementId(trackedGameId, eventType)] : []),
    getCalendarGroupElementId(trackedGameId, `${eventType}_${trackedGameId}`),
  ]

  return Array.from(new Set(candidates))
}

type HighlightedCalendarGroupRowHiddenParams = {
  highlightedEventId?: string
  /** Events of the calendar's loaded range. Undefined while nothing has loaded. */
  gameEvents?: Pick<TrackingEvent, 'eventId'>[]
  groupByBlueprint?: boolean
}

/**
 * True when, with the events loaded, the highlighted event has no instance among them, so no blueprint row of it will
 * mount (e.g. a filter removes it). Without grouping the type row always exists, so it is never hidden.
 *
 * A discontinued blueprint is not a reason: "Hide discontinued blueprints" does not apply to the blueprint of the
 * highlighted event (see `useCalendarEventsByGame`), so its row mounts like any other.
 */
export const isHighlightedCalendarGroupRowHidden = ({
  highlightedEventId,
  gameEvents,
  groupByBlueprint = false,
}: HighlightedCalendarGroupRowHiddenParams): boolean => {
  if (!groupByBlueprint || !highlightedEventId || !gameEvents) return false

  const instances = gameEvents.filter((event) => event.eventId === highlightedEventId)

  return instances.length === 0
}

/**
 * Returns the topmost of the candidate elements that is mounted in the document, so the page scrolls to the first
 * highlighted row even when the highlighted event spans several rows.
 */
export const findFirstHighlightedCalendarGroupElement = (elementIds: string[], root: Pick<Document, 'getElementById'> = document): HTMLElement | undefined => {
  let first: { element: HTMLElement; top: number } | undefined

  elementIds.forEach((elementId) => {
    const element = root.getElementById(elementId)
    if (!element) return

    const top = element.getBoundingClientRect().top
    if (!first || top < first.top) first = { element, top }
  })

  return first?.element
}

/**
 * Topmost group row of the given event type on the Games or Compare Events tab, whichever blueprint or game it belongs
 * to. A row matches only when the id is exactly `${gameId}-${typeId}` or continues with `_` right after the type id, so
 * a type id can't match another type id that starts with it. Used as a last resort when the highlighted event's own row
 * never mounts, e.g. because its blueprint is hidden.
 */
export const findEventTypeCalendarGroupElement = (
  trackedGameId: string | undefined,
  eventType: string | undefined,
  root: Pick<Document, 'querySelectorAll'> = document
): HTMLElement | undefined => {
  if (!trackedGameId || !eventType) return undefined

  const rowIdPrefix = getCalendarGroupElementId(trackedGameId, `${eventType}_`)
  const rowId = getCalendarGroupElementId(trackedGameId, eventType)
  let first: { element: HTMLElement; top: number } | undefined

  root.querySelectorAll<HTMLElement>('.LiveEventsCalendarTrackingEventGroup, .LiveEventsCalendarTrackingEventWithGameGroup').forEach((element) => {
    if (element.id !== rowId && !element.id.startsWith(rowIdPrefix)) return

    const top = element.getBoundingClientRect().top
    if (!first || top < first.top) first = { element, top }
  })

  return first?.element
}
